import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "@fontsource/poppins/300.css";
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/tajawal/400.css";
import "@fontsource/tajawal/500.css";
import ContactUs from "../components/ContactUs";
import Header from "../components/HeaderMui";
import useIsMobile from "../hooks/useIsMobile";
import useHeaderShrink from "../hooks/useHeaderShrink";
import useSwipeGesture from "../hooks/useSwipeGesture";
import useFetchMeals from "../hooks/useFetchMeals";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../logo_class.css";
import "./Home.css";
import { Box } from "@mui/material";

function scrollToSection(id) {
  const section = document.getElementById(id);
  const header = document.querySelector("header");

  if (section && header) {
    const headerHeight = header.offsetHeight;
    const sectionTop = section.getBoundingClientRect().top + window.pageYOffset;

    window.scrollTo({
      top: id === "home" ? 0 : sectionTop - headerHeight,
      behavior: "smooth",
    });
  }
}

function Home() {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const toggleMobileMenu = () => setShowMobileMenu((prev) => !prev);
  const isMobile = useIsMobile();
  const { headerHeight, isScrolled } = useHeaderShrink(isMobile);
  const { meals, loading } = useFetchMeals();
  const [showCartMenu, setShowCartMenu] = useState(false);
  const toggleCartMenu = () => setShowCartMenu((prev) => !prev);
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const { t, i18n } = useTranslation();
  const scrollToId = location.state?.scrollToId;
  const isRtl = i18n.language === "ar" || i18n.language === "he";

  useEffect(() => {
    // 1) only on PUSH/REPLACE
    if (navType === "POP" || !scrollToId) return;

    // 2) wait a tick so layout/fonts/images settle
    const timer = setTimeout(() => {
      const section = document.getElementById(scrollToId);
      const header = document.querySelector("header");
      if (section && header) {
        const top =
          scrollToId === "home" ? 0 : section.offsetTop - header.offsetHeight;
        window.scrollTo({ top, behavior: "smooth" });
      }
      // 3) clear the flag so it never runs again
      navigate(location.pathname, { replace: true, state: {} });
    }, 350);

    return () => clearTimeout(timer);
  }, [navType, scrollToId, navigate, location.pathname]);

  useSwipeGesture({
    onSwipeLeft: () => setShowMobileMenu(true),
    onSwipeRight: () => setShowMobileMenu(false),
    isMenuVisible: showMobileMenu,
  });

  const goToOrder = (mealId) => {
    navigate("/Order", mealId != null ? { state: { mealId } } : undefined);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="Home home-page" dir={isRtl ? "rtl" : "ltr"}>
      {/* Header */}
      <Header
        isScrolled={isScrolled}
        isMobile={isMobile}
        headerHeight={headerHeight}
        showMobileMenu={showMobileMenu}
        toggleMobileMenu={toggleMobileMenu}
        scrollToSection={scrollToSection}
        showCartMenu={showCartMenu}
        toggleCartMenu={toggleCartMenu}
      />
      <main
        style={{
          marginTop: isScrolled
            ? headerHeight
            : `calc(${headerHeight} + 2.85rem)`,
          transition: "margin-top 0.3s ease-out",
        }}
      >
        <section id="home" className="home-hero">
          <div className="home-hero__glow home-hero__glow--a" aria-hidden="true" />
          <div className="home-hero__glow home-hero__glow--b" aria-hidden="true" />
          <div className="home-hero__grid" aria-hidden="true" />
          <motion.div
            className="home-hero__content"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.h1
              className="home-hero__brand"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Diet Chef
            </motion.h1>
            <motion.p
              className="home-hero__headline"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.22, duration: 0.6 }}
            >
              {t("mealPreparation")}
            </motion.p>
            <motion.p
              className="home-hero__sub"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.34, duration: 0.6 }}
            >
              {t("dietChefDescription")}
            </motion.p>
            <motion.div
              className="home-hero__ctas"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.48, duration: 0.55 }}
            >
              <button
                type="button"
                className="home-btn home-btn--primary"
                onClick={() => goToOrder()}
              >
                {t("orderNow")}
              </button>
              <button
                type="button"
                className="home-btn home-btn--ghost"
                onClick={() => scrollToSection("meals")}
              >
                {t("exploreMeals")}
              </button>
            </motion.div>
          </motion.div>
        </section>
        <section id="how-it-works" className="home-section home-steps">
          <div className="home-section__inner home-steps__layout">
            <div className="home-steps__copy">
              <motion.h2
                className="home-section__title"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5 }}
              >
                {t("howItWorks")}
              </motion.h2>
              <div className="home-steps__grid">
                {[
                  {
                    n: "01",
                    title: t("firstStep"),
                    points: [t("firstStepA"), t("firstStepB")],
                  },
                  {
                    n: "02",
                    title: t("deliveryTime"),
                    points: [t("deliveryTimeA"), t("deliveryTimeB")],
                  },
                  {
                    n: "03",
                    title: t("thirdStep"),
                    points: [],
                  },
                ].map((step, i) => (
                  <motion.article
                    key={step.n}
                    className="home-step"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.45, delay: i * 0.1 }}
                  >
                    <span className="home-step__num">{step.n}</span>
                    <h3 className="home-step__title">{step.title}</h3>
                    {step.points.length > 0 && (
                      <ul className="home-step__list">
                        {step.points.map((p) => (
                          <li key={p}>{p}</li>
                        ))}
                      </ul>
                    )}
                  </motion.article>
                ))}
              </div>
            </div>
            <motion.div
              className="home-steps__visual"
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <img src="/meals3.jpeg" alt="" loading="lazy" />
            </motion.div>
          </div>
        </section>

        <section id="why-us" className="home-section home-why">
          <div className="home-section__inner">
            <motion.h2
              className="home-section__title"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5 }}
            >
              {t("whyChooseUs")}
            </motion.h2>
            <div className="home-why__grid">
              {[
                { text: t("whyUsA"), img: "/lunch.png" },
                { text: t("whyUsB"), img: "/hourglass.png" },
                { text: t("whyUsC"), img: "/dumbbell.png" },
                { text: t("whyUsD"), img: "/grocery.png" },
                { text: t("whyUsE"), img: "/calendar.png" },
                { text: t("whyUsF"), img: "/badge.png" },
                { text: t("whyUsG"), img: "/reputation.png" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="home-why__item"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.4,
                    delay: Math.min(index * 0.05, 0.3),
                  }}
                >
                  <div className="home-why__icon">
                    <img src={item.img} alt="" loading="lazy" />
                  </div>
                  <p>{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="meals" className="home-meals">
          <motion.h2
            className="home-meals__title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5 }}
          >
            {t("meals")}
          </motion.h2>
          <p className="home-meals__hint">{t("mealsTapHint")}</p>

          <div className="home-meals__wrap">
            {loading ? (
              <div
                style={{
                  padding: "1rem",
                  borderRadius: "12px",
                  width: "100%",
                  maxWidth: "400px",
                  display: "flex",
                  justifyContent: "center",
                  margin: "0 auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <img
                    src="/loadingAnimation1.svg"
                    alt="Loading animation"
                    style={{
                      width: "200px",
                      height: "200px",
                      display: "block",
                    }}
                  />
                  <p>{t("loading")}</p>
                </Box>
              </div>
            ) : (
              <div
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                <Slider
                  dots
                  infinite
                  speed={500}
                  slidesToShow={isMobile ? 1 : 3}
                  slidesToScroll={1}
                  autoplay
                  autoplaySpeed={3500}
                  pauseOnHover
                  arrows={!isMobile}
                  responsive={[
                    {
                      breakpoint: 1024,
                      settings: { slidesToShow: 2, slidesToScroll: 1 },
                    },
                    {
                      breakpoint: 600,
                      settings: { slidesToShow: 1, slidesToScroll: 1 },
                    },
                  ]}
                >
                  {(meals || []).map((meal) => {
                    const ingredients = Array.isArray(meal.ingredients)
                      ? meal.ingredients.slice(0, 4)
                      : [];
                    return (
                      <div key={meal.id} className="home-meal-slide">
                        <button
                          type="button"
                          className="home-meal-card"
                          onClick={() => goToOrder(meal.id)}
                          aria-label={`${meal.name} — ${t("orderNow")}`}
                        >
                          <div className="home-meal-card__media">
                            <img
                              src={
                                meal.imageUrl ||
                                "https://media.istockphoto.com/id/1071096328/photo/fresh-salmon-fillets.jpg?s=1024x1024&w=is&k=20&c=4xxD-j_ksWiJKP4z_oazsdBLG7BnkxzYsd0Z9HG2mKE="
                              }
                              alt={meal.name}
                              loading="lazy"
                            />
                            <span className="home-meal-card__badge">
                              {meal.calories} {t("caloriesShort")}
                            </span>
                          </div>
                          <div className="home-meal-card__body">
                            <h3 className="home-meal-card__name">
                              {meal.name}
                            </h3>
                            <div className="home-meal-card__macros">
                              <span className="home-meal-card__chip">
                                {t("protein")} {meal.protein}g
                              </span>
                              <span className="home-meal-card__chip">
                                {t("carbs")} {meal.carbs}g
                              </span>
                              <span className="home-meal-card__chip">
                                {t("fats")} {meal.fat}g
                              </span>
                            </div>
                            {ingredients.length > 0 && (
                              <ul className="home-meal-card__ingredients">
                                {ingredients.map((ingredient, index) => (
                                  <li key={index}>{ingredient}</li>
                                ))}
                              </ul>
                            )}
                            <div className="home-meal-card__cta">
                              <span>{t("orderNow")}</span>
                              <span className="home-meal-card__arrow" aria-hidden="true">
                                {isRtl ? "←" : "→"}
                              </span>
                            </div>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </Slider>
              </div>
            )}
          </div>
        </section>

        <ContactUs />
      </main>
    </div>
  );
}

export default Home;
